// SVG arrow primitives used by HorizontalArrows / VerticalArrows, the
// overlays composed with DivergingBarChart inside DivergingBarWithArrows.

type ArrowDirection = 'left' | 'right' | 'up' | 'down';

type ArrowProps = {
  direction: ArrowDirection;
  x: number;
  y: number;
  length?: number;
  color?: string;
  strokeWidth?: number;
  opacity?: number;
};

export const Arrow = ({
  direction,
  x,
  y,
  length = 40,
  color = '#2d426a',
  strokeWidth = 2,
  opacity = 0.7,
}: ArrowProps) => {
  const isHorizontal = direction === 'left' || direction === 'right';
  const isLeft = direction === 'left';
  const isUp = direction === 'up';
  const headSize = length * 0.2;

  if (isHorizontal) {
    const startX = isLeft ? x - length : x;
    const endX = isLeft ? x : x + length;

    return (
      <g>
        <line
          x1={startX}
          y1={y}
          x2={endX}
          y2={y}
          stroke={color}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
        <line
          x1={isLeft ? startX : endX}
          y1={y}
          x2={isLeft ? startX + headSize : endX - headSize}
          y2={y - headSize / 2}
          stroke={color}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
        <line
          x1={isLeft ? startX : endX}
          y1={y}
          x2={isLeft ? startX + headSize : endX - headSize}
          y2={y + headSize / 2}
          stroke={color}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      </g>
    );
  }

  // Vertical: up | down — tip at endY
  const startY = isUp ? y + length : y;
  const endY = isUp ? y : y + length;

  return (
    <g>
      <line
        x1={x}
        y1={startY}
        x2={x}
        y2={endY}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
      />
      <line
        x1={x}
        y1={endY}
        x2={x - headSize / 2}
        y2={isUp ? endY + headSize : endY - headSize}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
      />
      <line
        x1={x}
        y1={endY}
        x2={x + headSize / 2}
        y2={isUp ? endY + headSize : endY - headSize}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
      />
    </g>
  );
};

type LabeledHorizontalArrowProps = {
  direction: 'left' | 'right';
  x: number;
  y: number;
  label: string;
  fontSize?: number;
  color?: string;
  opacity?: number;
  maxWidth?: number;
};

export const LabeledHorizontalArrow = ({
  direction,
  x,
  y,
  label,
  fontSize = 12,
  color = '#2d426a',
  opacity = 0.7,
  maxWidth,
}: LabeledHorizontalArrowProps) => {
  const isLeft = direction === 'left';
  const labelWords = label.split(' ');
  const needsWrapping = maxWidth && labelWords.length > 1 && label.length > 10;
  const arrowLength = Math.min(40, maxWidth ? maxWidth / 3 : 40);

  return (
    <g transform={`translate(${x}, ${y})`}>
      <Arrow
        direction={direction}
        x={isLeft ? -10 : 10}
        y={needsWrapping ? 15 : 5}
        length={arrowLength}
        color={color}
        opacity={opacity}
      />

      {needsWrapping ? (
        <text
          x={0}
          y={0}
          textAnchor={isLeft ? 'start' : 'end'}
          fontSize={fontSize}
          fontWeight={600}
          fontFamily="'Source Sans Pro', 'Roboto'"
          fill={color}
          opacity={opacity}
        >
          <tspan x={0} dy="0em">
            {labelWords[0]}
          </tspan>
          <tspan x={0} dy="1.2em">
            {labelWords.slice(1).join(' ')}
          </tspan>
        </text>
      ) : (
        <text
          x={0}
          y={9}
          textAnchor={isLeft ? 'start' : 'end'}
          fontSize={fontSize}
          fontWeight={600}
          fontFamily="'Source Sans Pro', 'Roboto'"
          fill={color}
          opacity={opacity}
        >
          {label}
        </text>
      )}
    </g>
  );
};
